import React, { useState } from "react";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { StatePill, Panel } from "@/public/ui";

const spring = { type: "spring", stiffness: 120, damping: 20 };

/* Subtle chart-line backdrop (SVG, lightweight) */
export function MarketPulse({ className = "" }) {
  const reduce = useReducedMotion();
  const d = "M0,120 C60,90 90,140 140,110 C190,80 230,150 290,100 C350,55 380,130 440,95 C500,60 540,120 600,80";
  return (
    <svg className={className} viewBox="0 0 600 200" fill="none" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id="mp" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#06B6D4" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#06B6D4" stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path d={d} stroke="#06B6D4" strokeWidth="1.5"
        initial={reduce ? {} : { pathLength: 0 }} whileInView={reduce ? {} : { pathLength: 1 }}
        viewport={{ once: true }} transition={{ duration: 2, ease: "easeInOut" }} />
      <path d={`${d} L600,200 L0,200 Z`} fill="url(#mp)" opacity="0.6" />
    </svg>
  );
}

function Counter({ value, label, tone = "sub", delay = 0 }) {
  const reduce = useReducedMotion();
  const c = { coach: "text-tc-coach", amber: "text-tc-xp", sub: "text-tc-txt" }[tone];
  return (
    <motion.div initial={reduce ? {} : { opacity: 0, y: 10 }} whileInView={reduce ? {} : { opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ delay, duration: 0.5 }} className="text-center">
      <div className={`font-heading text-4xl sm:text-5xl font-extrabold ${c}`}>{value}</div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-tc-muted">{label}</div>
    </motion.div>
  );
}

/* HERO / ATTENTION — universe contracts into an attention set */
export function AttentionViz() {
  const situations = [
    { sym: "DOT/USD", note: "Breakout developing", state: "NEAR TRIGGER", pr: 82 },
    { sym: "UNI/USD", note: "Structure materially changed", state: "DEVELOPING", pr: 67 },
    { sym: "LINK/USD", note: "Reclaim above range high", state: "TRIGGERED", pr: 74 },
    { sym: "AVAX/USD", note: "Failed continuation", state: "INVALIDATED", pr: 31 },
    { sym: "ATOM/USD", note: "Base forming, low urgency", state: "EARLY", pr: 44 },
  ];
  return (
    <Panel glow="coach" className="p-5 sm:p-6" data-testid="viz-attention">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-tc-coach">Attention Queue · illustrative session</div>
        <span className="font-mono text-[10px] text-tc-muted">LIVE UNIVERSE → FOCUS</span>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-2 rounded-xl border border-white/10 bg-tc-bg/60 py-4">
        <Counter value="20" label="Markets screened" delay={0} />
        <Counter value="13" label="Deserve attention" tone="coach" delay={0.15} />
        <Counter value="7" label="Materially changed" tone="amber" delay={0.3} />
      </div>
      <div className="mt-5 space-y-2">
        {situations.map((s, i) => (
          <motion.div key={s.sym} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.25 + i * 0.08, ...spring }}
            className="flex items-center justify-between rounded-lg border border-white/8 bg-tc-elevated/40 px-3 py-2.5">
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-mono text-sm text-tc-txt">{s.sym}</span>
              <span className="text-xs text-tc-muted truncate hidden sm:inline">{s.note}</span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="hidden sm:flex items-center gap-1.5">
                <span className="font-mono text-[9px] text-tc-muted uppercase">Priority</span>
                <div className="w-14 h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <motion.div className="h-full bg-tc-coach rounded-full" initial={{ width: 0 }} whileInView={{ width: `${s.pr}%` }} viewport={{ once: true }} transition={{ delay: 0.4 + i * 0.08, duration: 0.6 }} />
                </div>
              </div>
              <StatePill>{s.state}</StatePill>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="mt-4 font-mono text-[10px] leading-relaxed text-tc-muted">
        No buy/sell signals · No predicted returns · Attention Priority is not a probability of profit
      </div>
    </Panel>
  );
}

/* ANALYST — objective evidence vs AI interpretation */
export function AnalystViz() {
  const objective = [
    ["Structure", "Higher-high / higher-low"],
    ["Trend (4H)", "Up · above rising 50MA"],
    ["Momentum", "RSI 61 · no divergence"],
    ["VWAP", "Reclaimed, holding"],
    ["Level", "Retest of prior range high"],
  ];
  const supporting = ["MTF alignment 1D/4H", "Volume expansion on reclaim", "R:R ≈ 1 : 2.8 to target"];
  const conflicting = ["1H momentum cooling", "Into weekly supply zone"];
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="viz-analyst">
      <Panel className="p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-tc-sub">Objective market evidence</div>
        <div className="mt-1 text-[11px] text-tc-muted">Deterministic · calculated, not opinion</div>
        <div className="mt-4 space-y-2">
          {objective.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between rounded-lg bg-tc-bg/50 border border-white/8 px-3 py-2">
              <span className="font-mono text-[11px] text-tc-muted uppercase tracking-wide">{k}</span>
              <span className="text-sm text-tc-txt">{v}</span>
            </div>
          ))}
        </div>
      </Panel>
      <Panel glow="violet" className="p-5">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-tc-mastery">AI interpretation</div>
          <StatePill>CONDITIONAL</StatePill>
        </div>
        <div className="mt-4">
          <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-tc-muted">Primary thesis</div>
          <p className="mt-1 text-sm text-tc-txt leading-relaxed">Continuation likely <span className="text-tc-sub">if</span> price holds the reclaimed range high on the retest.</p>
          <div className="mt-3 font-mono text-[9px] uppercase tracking-[0.16em] text-tc-muted">Alternative thesis</div>
          <p className="mt-1 text-sm text-tc-sub leading-relaxed">Failed reclaim → rotation back into range; stand aside.</p>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-wide text-tc-bull">Supporting</div>
            <ul className="mt-1.5 space-y-1">{supporting.map((s) => <li key={s} className="text-[12px] text-tc-sub flex gap-1.5"><span className="text-tc-bull">+</span>{s}</li>)}</ul>
          </div>
          <div>
            <div className="font-mono text-[9px] uppercase tracking-wide text-tc-xp">Conflicting</div>
            <ul className="mt-1.5 space-y-1">{conflicting.map((s) => <li key={s} className="text-[12px] text-tc-sub flex gap-1.5"><span className="text-tc-xp">–</span>{s}</li>)}</ul>
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-tc-bear/30 bg-tc-bear/5 px-3 py-2">
          <span className="font-mono text-[9px] uppercase tracking-wide text-tc-bear">Invalidation</span>
          <p className="text-[12px] text-tc-sub mt-0.5">Thesis is wrong on a 4H close back below the range high.</p>
        </div>
      </Panel>
    </div>
  );
}

/* HUMAN + AI — lock your read, then compare */
export function LockReadViz() {
  const [locked, setLocked] = useState(false);
  const yours = [["Bias", "Long"], ["Structure", "Uptrend"], ["Confidence", "Medium"], ["Wrong if", "Loses range high"]];
  const tc = [["Read", "Conditional long"], ["Agrees on", "Trend + structure"], ["Pushes back", "1H momentum cooling"], ["Invalidation", "4H close < range high"]];
  return (
    <div data-testid="viz-lockread">
      <div className="grid md:grid-cols-2 gap-4">
        <Panel className="p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-tc-coach">Your read — first</div>
          <div className="mt-4 space-y-2">
            {yours.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded-lg bg-tc-bg/50 border border-white/8 px-3 py-2">
                <span className="font-mono text-[11px] uppercase text-tc-muted">{k}</span><span className="text-sm text-tc-txt">{v}</span>
              </div>
            ))}
          </div>
        </Panel>
        <div className="relative">
          <AnimatePresence mode="wait">
            {!locked ? (
              <motion.div key="locked" initial={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="h-full rounded-2xl border border-dashed border-white/15 bg-tc-card/40 flex flex-col items-center justify-center p-8 text-center">
                <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-tc-muted">TRADECRAFT's analysis is hidden</div>
                <p className="mt-2 text-sm text-tc-sub max-w-xs">Commit to your own read before you see the machine's. That's the whole point.</p>
              </motion.div>
            ) : (
              <motion.div key="revealed" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
                <Panel glow="violet" className="p-5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-tc-mastery">TRADECRAFT — independent</div>
                  <div className="mt-4 space-y-2">
                    {tc.map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between rounded-lg bg-tc-bg/50 border border-white/8 px-3 py-2">
                        <span className="font-mono text-[11px] uppercase text-tc-muted">{k}</span><span className="text-sm text-tc-txt">{v}</span>
                      </div>
                    ))}
                  </div>
                </Panel>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <div className="mt-5 flex justify-center">
        <button data-testid="lock-read-btn" onClick={() => setLocked((v) => !v)}
          className="rounded-xl bg-tc-mastery text-white px-6 py-3 text-sm font-semibold hover:opacity-90 hover:shadow-[0_0_28px_rgba(139,92,246,0.35)] transition-all focus:outline-none focus:ring-2 focus:ring-tc-mastery focus:ring-offset-2 focus:ring-offset-tc-bg">
          {locked ? "Reset comparison" : "Lock my read & see TRADECRAFT's"}
        </button>
      </div>
    </div>
  );
}

/* CAPABILITY LADDER */
const RUNGS = [
  ["FOUNDATIONS", "Learn to read markets and manage risk."],
  ["COACH", "TRADECRAFT explains concepts and challenges your reasoning."],
  ["COPILOT", "You and TRADECRAFT analyze markets together."],
  ["ANALYST", "TRADECRAFT performs structured analysis for you to evaluate."],
  ["INTELLIGENCE", "TRADECRAFT scans live markets and surfaces what deserves attention."],
];
export function CapabilityLadder() {
  return (
    <div className="relative" data-testid="viz-ladder">
      <div className="absolute left-[19px] top-4 bottom-4 w-px bg-white/10 md:hidden" />
      <div className="grid md:grid-cols-5 gap-4">
        {RUNGS.map(([name, desc], i) => (
          <motion.div key={name} initial={{ opacity: 0.25, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }} transition={{ delay: i * 0.12, duration: 0.6 }}
            className="relative flex md:block items-start gap-4">
            <div className="flex md:mb-3 items-center justify-center w-10 h-10 shrink-0 rounded-lg border border-tc-coach/40 bg-tc-coach/10 font-mono text-sm text-tc-coach">{i + 1}</div>
            <div>
              <div className="font-heading font-bold tracking-tight text-tc-txt">{name}</div>
              <p className="mt-1 text-[13px] leading-relaxed text-tc-sub">{desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* PROCESS SCORE — radial + dimensions */
export function ProcessScoreViz() {
  const reduce = useReducedMotion();
  const score = 74;
  const dims = [["Setup", 82], ["Risk", 88], ["Plan", 71], ["Execution", 64], ["Psychology", 70], ["Reflection", 79]];
  const R = 52, C = 2 * Math.PI * R;
  return (
    <Panel className="p-6 grid sm:grid-cols-2 gap-6 items-center" data-testid="viz-process">
      <div className="flex flex-col items-center">
        <svg width="150" height="150" viewBox="0 0 150 150">
          <circle cx="75" cy="75" r={R} stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
          <motion.circle cx="75" cy="75" r={R} stroke="#06B6D4" strokeWidth="10" fill="none" strokeLinecap="round"
            transform="rotate(-90 75 75)" strokeDasharray={C}
            initial={reduce ? { strokeDashoffset: C * (1 - score / 100) } : { strokeDashoffset: C }}
            whileInView={{ strokeDashoffset: C * (1 - score / 100) }} viewport={{ once: true }} transition={{ duration: 1.4, ease: "easeOut" }} />
          <text x="75" y="70" textAnchor="middle" className="fill-tc-txt font-heading" fontSize="30" fontWeight="800">{score}</text>
          <text x="75" y="92" textAnchor="middle" className="fill-tc-muted font-mono" fontSize="10">/ 100</text>
        </svg>
        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-tc-coach">Process Score</div>
        <div className="mt-4 flex gap-2">
          <span className="rounded-md border border-tc-bull/40 text-tc-bull px-2 py-0.5 font-mono text-[10px]">LONG</span>
          <span className="rounded-md border border-tc-bear/40 text-tc-bear px-2 py-0.5 font-mono text-[10px]">SHORT</span>
          <span className="rounded-md border border-tc-pass/50 text-tc-pass px-2 py-0.5 font-mono text-[10px]">PASS</span>
        </div>
      </div>
      <div className="space-y-2.5">
        {dims.map(([k, v], i) => (
          <div key={k}>
            <div className="flex justify-between font-mono text-[10px] uppercase tracking-wide text-tc-muted"><span>{k}</span><span className="text-tc-sub">{v}</span></div>
            <div className="mt-1 h-1.5 rounded-full bg-white/8 overflow-hidden">
              <motion.div className="h-full rounded-full bg-gradient-to-r from-tc-coach to-tc-mastery" initial={{ width: 0 }} whileInView={{ width: `${v}%` }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.7 }} />
            </div>
          </div>
        ))}
        <p className="pt-1 font-mono text-[10px] text-tc-muted leading-relaxed">Profit contributes zero to the educational Process Score. A good decision can lose; a bad decision can win.</p>
      </div>
    </Panel>
  );
}

/* DAILY — assistance modes change depth, not access */
export function DailyViz() {
  const [mode, setMode] = useState("SUPPORTED");
  const modes = ["GUIDED", "SUPPORTED", "INDEPENDENT"];
  const depth = {
    GUIDED: "Full step-by-step explanation with the reasoning spelled out.",
    SUPPORTED: "Key evidence and the read, with the details a tap away.",
    INDEPENDENT: "Just the evidence and the situation. You draw the conclusion.",
  };
  const views = [
    ["What deserves attention", "3 situations surfaced from 20 markets"],
    ["What changed", "UNI structure materially changed overnight"],
    ["Your next move", "Lock a read on DOT before it triggers"],
    ["What to learn", "Multi-timeframe confluence · 1 gap detected"],
    ["Where you're developing", "Risk 88 · Execution 64 — focus here"],
  ];
  return (
    <Panel className="p-6" data-testid="viz-daily">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-tc-coach">TRADECRAFT Daily</div>
        <div className="flex rounded-lg border border-white/10 p-0.5 bg-tc-bg/60">
          {modes.map((m) => (
            <button key={m} data-testid={`daily-mode-${m.toLowerCase()}`} onClick={() => setMode(m)}
              className={`px-3 py-1 rounded-md font-mono text-[10px] tracking-wide transition-colors ${mode === m ? "bg-tc-coach text-tc-bg" : "text-tc-muted hover:text-tc-txt"}`}>{m}</button>
          ))}
        </div>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-2.5">
        {views.map(([k, v]) => (
          <div key={k} className="rounded-lg border border-white/8 bg-tc-bg/50 px-3 py-2.5">
            <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-tc-sub">{k}</div>
            <div className="mt-1 text-[13px] text-tc-txt leading-snug">{v}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-lg border border-tc-coach/25 bg-tc-coach/5 px-3 py-2.5">
        <span className="font-mono text-[9px] uppercase tracking-wide text-tc-coach">{mode}</span>
        <p className="text-[13px] text-tc-sub mt-0.5">{depth[mode]}</p>
      </div>
      <p className="mt-3 font-mono text-[10px] text-tc-muted">Same evidence. Different level of guidance — assistance never changes access or market facts.</p>
    </Panel>
  );
}
